import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import Debug from 'debug';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as fromActions from '../actions/auth.actions';
import { CreateUserDto } from '../dtos/createUserDto.interface';
import { GetUserDto } from '../dtos/getUserDto.type';
import { Role } from '../enums/role.enum';
import { UpdateUserDto } from '../dtos/updateUserDto.type';
import { IChangePasswordRequest } from '../interfaces/change-password-request.interface';
import { ILoginRequest } from '../interfaces/login-request.interface';
import { IResetUserRequest } from '../interfaces/reset-user-request.interface';
import { ISetOrResetPasswordRequest } from '../interfaces/set-or-reset-password-request.interface';
import { IVerifyOOBCode } from '../interfaces/verifyOOBCode.interface';
import * as fromFeature from '../reducers/auth.reducer';
import { AuthService } from '../services/auth.service';

import { IUserPreferences } from '../interfaces/user-preferences.interface';
import { IAuthProvider } from './IAuthProvider.interface';

const debug = Debug('aumico:aumico-lib-users-fe:UsersProvider');

@Injectable({
    providedIn: 'root',
})
export class AuthProvider implements IAuthProvider {
    constructor(private readonly store: Store<fromFeature.AppState>, private readonly service: AuthService) {
        debug('provider constructed');
    }

    login(user: ILoginRequest): void {
        this.store.dispatch(fromActions.login.do({ payload: user }));
    }

    dispatchGetCurrentUser(): void {
        this.store.dispatch(fromActions.getCurrentUser.do());
    }

    getCurrentUser$(): Observable<GetUserDto> {
        return this.store.select(fromFeature.selectCurrentUser);
    }

    /**
     * True when the current user is an org or app admin. Single source of truth so
     * pages don't re-implement the role check.
     *
     * Reads the roles from the Firebase ID-token claims (via
     * `getCurrentUserRoles$()`), NOT the NgRx `currentUser` DTO. The token claim
     * is the canonical admin signal in this app — the sidenav "Organization
     * settings" item and the `AdminGuard` route gate both key off the same
     * `getCurrentUserRoles$()` / `claims['roles']`. The BE `/users/current` DTO's
     * `roles` array is NOT guaranteed to carry `ORG_ADMIN` (org-admin can live in
     * its separate `organizationRole` field), so deriving admin-ness from the
     * store DTO made this return false for genuine org admins while the sidenav
     * correctly showed them as admin. Keep this aligned with the sidenav.
     */
    getIsAdmin$(): Observable<boolean> {
        return this.getCurrentUserRoles$().pipe(
            map((roles) => (roles ?? []).some((r) => r === Role.ORG_ADMIN || r === Role.APP_ADMIN)),
        );
    }

    getCurrentUserRoles$(): Observable<string[]> {
        return this.service.getCurrentUserRoles();
    }

    dispatchUpdateCurrentUser(user: UpdateUserDto): void {
        this.store.dispatch(fromActions.updateCurrentUser.do({ payload: user }));
    }

    setCurrentUser(user: Partial<GetUserDto>): void {
        this.store.dispatch(fromActions.setCurrentUser({ payload: user }));
    }

    logout(): void {
        this.store.dispatch(fromActions.logout.do());
    }

    dispatchSignUp(user: CreateUserDto): void {
        this.store.dispatch(fromActions.signup.do({ createUserDto: user }));
    }

    dispatchResendVerificationMail(email: string): void {
        this.store.dispatch(fromActions.sendVerificationMail.do({ payload: email }));
    }
    dispatchSendResetEmail(user: IResetUserRequest): void {
        this.store.dispatch(fromActions.password.sendEmailForReset({ payload: user }));
    }
    dispatchChangePassword(changePasswordForm: IChangePasswordRequest): void {
        this.store.dispatch(fromActions.password.change({ payload: changePasswordForm }));
    }
    dispatchVerifyOOB(verifyOOBCode: IVerifyOOBCode): void {
        this.store.dispatch(fromActions.verifyOOBCode.do({ payload: verifyOOBCode }));
    }
    dispatchSetPassword(setOrResetPasswordData: ISetOrResetPasswordRequest): void {
        this.store.dispatch(fromActions.password.set({ payload: setOrResetPasswordData }));
    }
    dispatchResetPassword(setOrResetPasswordData: ISetOrResetPasswordRequest): void {
        this.store.dispatch(fromActions.password.update({ payload: setOrResetPasswordData }));
    }
    getUsersError$(): Observable<HttpErrorResponse> {
        return this.store.select(fromFeature.selectError) as Observable<HttpErrorResponse>;
    }
    dispatchAgreeToTerms(): void {
        this.store.dispatch(fromActions.agreeToTerms.do());
    }
    dispatchUpdateUserPreferences(preferences: IUserPreferences): void {
        this.store.dispatch(fromActions.updateUserPreferences.do({ payload: preferences }));
    }
}
